import React from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';

import {
  SURFACE_PADDING,
  GAP_SCALE,
  SIZE_SCALE,
  POSITION_SCALE,
  RADIUS_SCALE,
} from '@/components/design-system/tokens';
import { buttonVariants } from '@/components/design-system/variants';
import { cn } from '@/lib/utils';

interface CalendarProps extends Omit<DayPickerProps, 'className' | 'classNames'> {
  className?: string;
  classNames?: Record<string, string>;
  showOutsideDays?: boolean;
}

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      mode={undefined}
      {...props}
      className={cn(`${SURFACE_PADDING['3']}`, className)}
      classNames={{
        months: `flex flex-col sm:flex-row ${GAP_SCALE.md} sm:${GAP_SCALE.md}`,
        month: `flex flex-col ${GAP_SCALE.md}`,
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: `${GAP_SCALE.xs} flex items-center`,
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          `${SIZE_SCALE['7']} bg-transparent ${SURFACE_PADDING.none} opacity-50 hover:opacity-100`
        ),
        nav_button_previous: `absolute ${POSITION_SCALE.left['1']}`,
        nav_button_next: `absolute ${POSITION_SCALE.right['1']}`,
        table: 'w-full border-collapse',
        head_row: 'flex',
        head_cell: `text-muted-foreground ${RADIUS_SCALE.md} ${SIZE_SCALE.w8} font-normal text-xs`,
        row: `flex ${SIZE_SCALE.full} mt-2`,
        cell: cn(
          `relative ${SURFACE_PADDING.none} text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md`,
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:${RADIUS_SCALE.md}'
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          `${SIZE_SCALE['8']} ${SIZE_SCALE.w8} ${SURFACE_PADDING.none} font-normal aria-selected:opacity-100`
        ),
        day_range_start: 'day-range-start',
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{}}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
